import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { PageHeader } from "@/components/layout/PageHeader";
import { MeasurementSilhouette } from "@/components/measurements/MeasurementSilhouette";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { toast } from "sonner";
import { formatDateLong, formatTimeRange } from "@/lib/format/date";
import { cn } from "@/lib/utils";
import {
  ArrowLeft,
  Calendar,
  Clock,
  MapPin,
  User,
  Phone,
  Loader2,
  Navigation,
  Check,
  Ruler,
  Send,
} from "lucide-react";

const measurementSchema = z.object({
  bustCm: z.number().min(50, "Must be at least 50cm").max(200, "Must be under 200cm"),
  waistCm: z.number().min(40, "Must be at least 40cm").max(200, "Must be under 200cm"),
  hipCm: z.number().min(50, "Must be at least 50cm").max(200, "Must be under 200cm"),
  shoulderCm: z.number().min(20, "Must be at least 20cm").max(100, "Must be under 100cm"),
  sleeveLengthCm: z.number().min(30, "Must be at least 30cm").max(100, "Must be under 100cm"),
  dressLengthCm: z.number().min(50, "Must be at least 50cm").max(200, "Must be under 200cm"),
  notes: z.string().optional(),
});

type MeasurementFormData = z.infer<typeof measurementSchema>;

interface Appointment {
  id: string;
  status: string;
  otp?: string;
  slot: {
    startAt: string;
    endAt: string;
  };
  area: {
    name: string;
  };
  address: {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    pincode: string;
  };
  customer: {
    publicId: string;
    name: string;
    phone?: string;
  };
  assignedExec?: {
    publicId: string;
    name: string;
  } | null;
}

const statusConfig: Record<string, { color: string; label: string; next?: string; nextLabel?: string }> = {
  ASSIGNED: { color: "bg-cyan-100 text-cyan-700", label: "Assigned", next: "EN_ROUTE", nextLabel: "Start Journey" },
  EN_ROUTE: { color: "bg-orange-100 text-orange-700", label: "En Route", next: "ARRIVED", nextLabel: "Mark Arrived" },
  ARRIVED: { color: "bg-teal-100 text-teal-700", label: "Arrived", next: "IN_PROGRESS", nextLabel: "Start Measurement" },
  IN_PROGRESS: { color: "bg-indigo-100 text-indigo-700", label: "In Progress" },
  MEASUREMENTS_SUBMITTED: { color: "bg-blue-100 text-blue-700", label: "Submitted" },
  PENDING_CUSTOMER_ACCEPTANCE: { color: "bg-amber-100 text-amber-700", label: "Pending Acceptance" },
};

const statusSteps = ["ASSIGNED", "EN_ROUTE", "ARRIVED", "IN_PROGRESS"];

export default function StaffMeasurementVisitPage() {
  const navigate = useNavigate();
  const params = useParams();
  const appointmentId = params.appointmentId as string;

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [appointment, setAppointment] = useState<Appointment | null>(null);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);

  const form = useForm<MeasurementFormData>({
    resolver: zodResolver(measurementSchema),
    defaultValues: {
      bustCm: 0,
      waistCm: 0,
      hipCm: 0,
      shoulderCm: 0,
      sleeveLengthCm: 0,
      dressLengthCm: 0,
      notes: "",
    },
  });

  const watchedValues = form.watch();
  const hasAllMeasurements =
    watchedValues.bustCm > 0 &&
    watchedValues.waistCm > 0 &&
    watchedValues.hipCm > 0 &&
    watchedValues.shoulderCm > 0 &&
    watchedValues.sleeveLengthCm > 0 &&
    watchedValues.dressLengthCm > 0;

  useEffect(() => {
    fetchAppointment();
  }, [appointmentId]);

  async function fetchAppointment() {
    try {
      const response = await fetch(`/api/customer/appointments/${appointmentId}`);
      if (!response.ok) throw new Error("Failed to fetch appointment");
      const data = await response.json();
      setAppointment(data.appointment);
    } catch (error) {
      toast.error("Failed to load appointment");
      navigate("/staff/measurements");
    } finally {
      setLoading(false);
    }
  }

  async function handleStatusUpdate(toStatus: string) {
    setSubmitting(true);
    try {
      const response = await fetch(`/api/staff/appointments/${appointmentId}/status`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ toStatus }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to update status");
      }

      toast.success(`Status updated to ${toStatus.replace(/_/g, " ")}`);
      await fetchAppointment();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to update");
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSubmitMeasurements(data: MeasurementFormData) {
    setSubmitting(true);
    try {
      const response = await fetch(
        `/api/staff/appointments/${appointmentId}/measurements/submit`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to submit measurements");
      }

      toast.success("Measurements submitted successfully!");
      navigate("/staff/measurements");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to submit");
    } finally {
      setSubmitting(false);
      setShowSubmitConfirm(false);
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title="Measurement Visit" />
        <div className="grid gap-6 lg:grid-cols-2">
          <Skeleton className="h-[400px]" />
          <Skeleton className="h-[400px]" />
        </div>
      </div>
    );
  }

  if (!appointment) return null;

  const status = statusConfig[appointment.status] || statusConfig.ASSIGNED;
  const currentStepIndex = statusSteps.indexOf(appointment.status);
  const canCaptureMeasurements = appointment.status === "IN_PROGRESS";

  return (
    <div className="space-y-6">
      <PageHeader
        title="Measurement Visit"
        subtitle={`${appointment.customer.name} (${appointment.customer.publicId})`}
        actions={
          <Button variant="outline" onClick={() => navigate("/staff/measurements")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        }
      />

      <div className="flex items-center justify-center gap-2 mb-6 overflow-x-auto pb-2">
        {statusSteps.map((step, index) => {
          const isCompleted = index < currentStepIndex;
          const isCurrent = index === currentStepIndex;

          return (
            <div key={step} className="flex items-center">
              <div
                className={cn(
                  "flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors",
                  isCompleted && "bg-emerald-100 text-emerald-700",
                  isCurrent && "bg-primary text-primary-foreground",
                  !isCompleted && !isCurrent && "bg-muted text-muted-foreground"
                )}
              >
                {isCompleted ? (
                  <Check className="h-4 w-4" />
                ) : (
                  <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-xs">
                    {index + 1}
                  </span>
                )}
                <span className="hidden sm:inline">
                  {step.replace(/_/g, " ")}
                </span>
              </div>
              {index < statusSteps.length - 1 && (
                <div
                  className={cn(
                    "w-8 h-0.5 mx-1",
                    index < currentStepIndex ? "bg-emerald-300" : "bg-muted"
                  )}
                />
              )}
            </div>
          );
        })}
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-6">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Visit Details</CardTitle>
                <Badge className={status.color}>{status.label}</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="flex items-start gap-3">
                  <User className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Customer</p>
                    <p className="font-medium">{appointment.customer.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {appointment.customer.publicId}
                    </p>
                  </div>
                </div>
                {appointment.customer.phone && (
                  <div className="flex items-start gap-3">
                    <Phone className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="text-sm text-muted-foreground">Phone</p>
                      <a
                        href={`tel:${appointment.customer.phone}`}
                        className="font-medium text-primary hover:underline"
                      >
                        {appointment.customer.phone}
                      </a>
                    </div>
                  </div>
                )}
              </div>

              <Separator />

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="flex items-start gap-3">
                  <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Date</p>
                    <p className="font-medium">
                      {formatDateLong(appointment.slot.startAt)}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Clock className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Time</p>
                    <p className="font-medium">
                      {formatTimeRange(appointment.slot.startAt, appointment.slot.endAt)}
                    </p>
                  </div>
                </div>
              </div>

              <Separator />

              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-muted-foreground">Address</p>
                  <p className="font-medium">{appointment.address.line1}</p>
                  {appointment.address.line2 && (
                    <p className="text-sm">{appointment.address.line2}</p>
                  )}
                  <p className="text-sm text-muted-foreground">
                    {appointment.address.city}, {appointment.address.state} -{" "}
                    {appointment.address.pincode}
                  </p>
                </div>
              </div>

              {status.next && (
                <Button
                  className="w-full mt-4"
                  onClick={() => handleStatusUpdate(status.next!)}
                  disabled={submitting}
                >
                  {submitting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Navigation className="h-4 w-4 mr-2" />
                  )}
                  {status.nextLabel}
                </Button>
              )}
            </CardContent>
          </Card>

          {canCaptureMeasurements && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Ruler className="h-4 w-4" />
                  Capture Measurements
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Form {...form}>
                  <form className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="bustCm"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Bust (cm)</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                step="0.5"
                                placeholder="92"
                                {...field}
                                onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="waistCm"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Waist (cm)</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                step="0.5"
                                placeholder="76"
                                {...field}
                                onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="hipCm"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Hip (cm)</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                step="0.5"
                                placeholder="98"
                                {...field}
                                onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="shoulderCm"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Shoulder (cm)</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                step="0.5"
                                placeholder="38"
                                {...field}
                                onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="sleeveLengthCm"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Sleeve Length (cm)</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                step="0.5"
                                placeholder="58"
                                {...field}
                                onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="dressLengthCm"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Dress Length (cm)</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                step="0.5"
                                placeholder="102"
                                {...field}
                                onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <FormField
                      control={form.control}
                      name="notes"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Notes (optional)</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="Any observations or special instructions..."
                              rows={3}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button
                      type="button"
                      className="w-full"
                      onClick={() => setShowSubmitConfirm(true)}
                      disabled={!hasAllMeasurements || submitting}
                    >
                      <Send className="h-4 w-4 mr-2" />
                      Submit Measurements
                    </Button>
                  </form>
                </Form>
              </CardContent>
            </Card>
          )}
        </div>

        <div>
          {hasAllMeasurements && (
            <MeasurementSilhouette
              data={{
                bust: watchedValues.bustCm,
                waist: watchedValues.waistCm,
                hip: watchedValues.hipCm,
                shoulder: watchedValues.shoulderCm,
                sleeveLength: watchedValues.sleeveLengthCm,
                dressLength: watchedValues.dressLengthCm,
                unit: "cm",
              }}
            />
          )}

          {!hasAllMeasurements && canCaptureMeasurements && (
            <Card className="h-full flex items-center justify-center">
              <CardContent className="py-12 text-center text-muted-foreground">
                <Ruler className="h-12 w-12 mx-auto mb-4 opacity-20" />
                <p>Enter all measurements to see the preview</p>
              </CardContent>
            </Card>
          )}

          {!canCaptureMeasurements && (
            <Card className="h-full flex items-center justify-center">
              <CardContent className="py-12 text-center text-muted-foreground">
                <Ruler className="h-12 w-12 mx-auto mb-4 opacity-20" />
                <p>
                  {appointment.status === "IN_PROGRESS"
                    ? "Ready to capture measurements"
                    : "Progress through the visit steps to capture measurements"}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={showSubmitConfirm}
        onOpenChange={setShowSubmitConfirm}
        title="Submit Measurements"
        description="Are you sure you want to submit these measurements? The customer will be notified to review and accept them."
        confirmLabel="Submit"
        onConfirm={() => form.handleSubmit(handleSubmitMeasurements)()}
        loading={submitting}
      />
    </div>
  );
}
